import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Handshake,
  Zap,
  ShieldCheck,
  Hammer,
} from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const values = [
  { icon: Handshake, title: 'Loyalty First', desc: 'We build long-term relationships, not one-off deals. The better your deals perform, the more we back you.' },
  { icon: Zap, title: 'Speed Matters', desc: 'Offers in hours, not days. Funding moves as fast as your deal needs it to.' },
  { icon: ShieldCheck, title: 'Radical Transparency', desc: 'No hiddn fees. No games. What you\'re offered is what you get.' },
  { icon: Hammer, title: 'Built for Builders', desc: 'Built by operators who understand the value of working capital and step in when banks won\'t. Fast decisions. Flexible funding. Real underwriting.' },
];

const expectations = [
  {
    text: 'Funding from $20K-$1M',
    image: '/images/offline/photo-1556740749-887f6717d7e4.jpg',
    alt: 'Business funding support',
  },
  {
    text: 'Terms from 3-24 months',
    image: '/images/offline/photo-1454165804606-c3d57bc86b40.jpg',
    alt: 'Business meeting about terms',
  },
  {
    text: 'AI-driven underwriting',
    image: '/images/offline/photo-1519389950473-47ba0277781c.jpg',
    alt: 'High tech office with green plants',
  },
  {
    text: '1st-5th positions',
    image: '/images/offline/photo-1504307651254-35680f356dfd.jpg',
    alt: 'Commercial funding position',
  },
  {
    text: 'A, B & C paper',
    image: '/images/offline/photo-1554224154-22dec7ec8818.jpg',
    alt: 'Business paperwork and approval review',
  },
  {
    text: 'Offers in under 2 hours',
    image: '/images/offline/photo-1516321318423-f06f85e504b3.jpg',
    alt: 'Fast business approval process',
  },
];

export default function WhatWeOffer() {
  return (
    <main className="bg-white text-gray-900">
      <HeroSection
        tag="Funding"
        title="Flexible funding built for your business"
        subtitle="Fast, flexible funding designed to match your business needs and growth."
        image="/images/offline/funding-terms-meeting-modern.jpg"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/apply" className="cta-primary">
              Apply for Funding <ArrowRight size={16} />
            </Link>
            <Link to="/partner" className="cta-secondary">
              Partner with Us
            </Link>
          </div>
        </div>
      </HeroSection>

      <section className="page-section bg-[#F8F7F4]">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide text-center"
          >
            <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">What Drives Us</span>
            <h2 className="mt-2 text-4xl font-bold text-gray-900">How We Work</h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-5 md:grid-cols-2 lg:grid-cols-4 lg:gap-6"
          >
            {values.map((v) => {
              const Icon = v.icon;

              return (
                <motion.div
                  key={v.title}
                  variants={fadeUp}
                  className="rounded-[28px] border border-gray-200 bg-white p-6 text-center shadow-[0_10px_35px_rgba(15,23,42,0.06)] md:p-7"
                >
                  <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0F172A] text-amber-400">
                    <Icon size={26} />
                  </div>
                  <h3 className="mb-2 font-bold text-gray-900">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-700">{v.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="page-section bg-white">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide text-center"
          >
            <h2 className="text-4xl font-bold text-gray-900">What You Can Expect</h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6"
          >
            {expectations.map((item) => (
              <motion.div
                key={item.text}
                variants={fadeUp}
                className="group relative h-[240px] overflow-hidden rounded-[28px] border border-white/10 shadow-[0_18px_50px_rgba(0,0,0,0.14)] sm:h-[260px]"
              >
                <img
                  src={item.image}
                  alt={item.alt}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-[#0F172A]/45 to-black/10" />

                <div className="relative flex h-full items-end p-5 sm:p-6 md:p-7">
                  <h3 className="text-2xl font-semibold leading-tight text-white md:text-3xl">
                    {item.text}
                  </h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
